import type { AxiosResponse } from 'axios'

import shippingLabelsQueries from '@/api/modules/shippingLabelsQueries'
import type {
    BoxTypesResponseSchema,
    CampaignsForShippingLabelsResponseSchema,
    CountriesResponseSchema,
    CreateAdditionalShippingLabelPayloadSchema,
    CreateCarrierLabelPayloadSchema,
    CreateShippingLabelFromCampaignPayloadSchema,
    CreateShippingLabelFromEcommerceOrderPayloadSchema,
    CreateShippingLabelFromShippingOrderPayloadSchema,
    CustomerAddressesResponseSchema,
    EcommerceOrdersForShippingLabelsResponseSchema,
    PackageTypesResponseSchema,
    PrintBulkSsccLabelsPayloadSchema,
    PrintShippingLabelPayloadSchema,
    PrintSsccLabelPayloadSchema,
    ShippingLabelCreateResponse,
    ShippingLabelDetails,
    ShippingLabelDestinationsResponseSchema,
    ShippingLabelStatusesResponseSchema,
    ShippingLabelTemplatesResponseSchema,
    ShippingLabelTypesResponseSchema,
    ShippingLabelsFilters,
    ShippingLabelsListResponseSchema,
    ShippingOrdersForShippingLabelsResponseSchema,
    ShippingLabelPackageConfigurationPayloadSchema,
    UpdateShippingLabelGoodPayloadSchema,
    UpdateShippingLabelPayloadSchema
} from '@/api/schemas/shipments/shippingLabels'

import type { ShippingLabelsState } from './state'
import { createInitialFilters } from './state'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createShippingLabelsActions(state: ShippingLabelsState) {
    const fetchShippingLabels = async (queryString = ''): Promise<AxiosResponse<ShippingLabelsListResponseSchema>> => {
        try {
            state.shippingLabelsLoading.value = true
            const response = await shippingLabelsQueries.fetchShippingLabels(queryString)
            state.shippingLabels.value = response.data.results || []
            return response
        } finally {
            state.shippingLabelsLoading.value = false
        }
    }

    const fetchShippingLabel = async (id: number | string): Promise<AxiosResponse<ShippingLabelDetails>> => {
        try {
            state.shippingLabelLoading.value = true
            const response = await shippingLabelsQueries.fetchShippingLabel(id)
            state.shippingLabel.value = response.data
            return response
        } finally {
            state.shippingLabelLoading.value = false
        }
    }

    const fetchShippingLabelStatuses = async (): Promise<AxiosResponse<ShippingLabelStatusesResponseSchema>> => {
        try {
            state.shippingLabelStatusesLoading.value = true
            const response = await shippingLabelsQueries.fetchShippingLabelStatuses()
            state.shippingLabelStatuses.value = response.data.results || []
            return response
        } finally {
            state.shippingLabelStatusesLoading.value = false
        }
    }

    const fetchShippingLabelTypes = async (): Promise<AxiosResponse<ShippingLabelTypesResponseSchema>> => {
        try {
            state.shippingLabelTypesLoading.value = true
            const response = await shippingLabelsQueries.fetchShippingLabelTypes()
            state.shippingLabelTypes.value = response.data.results || []
            return response
        } finally {
            state.shippingLabelTypesLoading.value = false
        }
    }

    const fetchShippingLabelTemplates = async (
        queryString = ''
    ): Promise<AxiosResponse<ShippingLabelTemplatesResponseSchema>> => {
        try {
            state.shippingLabelTemplatesLoading.value = true
            const response = await shippingLabelsQueries.fetchShippingLabelTemplates(queryString)
            state.shippingLabelTemplates.value = response.data.results || []
            return response
        } finally {
            state.shippingLabelTemplatesLoading.value = false
        }
    }

    const fetchShippingLabelDestinations = async (): Promise<AxiosResponse<ShippingLabelDestinationsResponseSchema>> => {
        try {
            state.shippingLabelDestinationsLoading.value = true
            const response = await shippingLabelsQueries.fetchShippingLabelDestinations()
            state.shippingLabelDestinations.value = response.data.results || []
            return response
        } finally {
            state.shippingLabelDestinationsLoading.value = false
        }
    }

    const fetchShippingOrdersForShippingLabels = async (
        queryString = ''
    ): Promise<AxiosResponse<ShippingOrdersForShippingLabelsResponseSchema>> => {
        try {
            state.shippingOrdersForShippingLabelsLoading.value = true
            const response = await shippingLabelsQueries.fetchShippingOrdersForShippingLabels(queryString)
            state.shippingOrdersForShippingLabels.value = response.data.results || []
            return response
        } finally {
            state.shippingOrdersForShippingLabelsLoading.value = false
        }
    }

    const fetchOrdersForShippingLabels = async (
        queryString = ''
    ): Promise<AxiosResponse<EcommerceOrdersForShippingLabelsResponseSchema>> => {
        try {
            state.ecommerceOrdersForShippingLabelsLoading.value = true
            const response = await shippingLabelsQueries.fetchOrdersForShippingLabels(queryString)
            state.ecommerceOrdersForShippingLabels.value = response.data.results || []
            return response
        } finally {
            state.ecommerceOrdersForShippingLabelsLoading.value = false
        }
    }

    const fetchCampaignsForShippingLabel = async (
        queryString = ''
    ): Promise<AxiosResponse<CampaignsForShippingLabelsResponseSchema>> => {
        try {
            state.campaignsForShippingLabelsLoading.value = true
            const response = await shippingLabelsQueries.fetchCampaignsForShippingLabel(queryString)
            state.campaignsForShippingLabels.value = response.data.results || []
            return response
        } finally {
            state.campaignsForShippingLabelsLoading.value = false
        }
    }

    const fetchBoxTypes = async (): Promise<AxiosResponse<BoxTypesResponseSchema>> => {
        try {
            state.boxTypesLoading.value = true
            const response = await shippingLabelsQueries.fetchBoxTypes()
            state.boxTypes.value = response.data.results || []
            return response
        } finally {
            state.boxTypesLoading.value = false
        }
    }

    const fetchPackageTypes = async (queryString = ''): Promise<AxiosResponse<PackageTypesResponseSchema>> => {
        try {
            state.packageTypesLoading.value = true
            const response = await shippingLabelsQueries.fetchPackageTypes(queryString)
            state.packageTypes.value = response.data.results || []
            return response
        } finally {
            state.packageTypesLoading.value = false
        }
    }

    const fetchCountries = async (): Promise<AxiosResponse<CountriesResponseSchema>> => {
        try {
            state.countriesLoading.value = true
            const response = await shippingLabelsQueries.fetchCountries()
            state.countries.value = response.data.results || []
            return response
        } finally {
            state.countriesLoading.value = false
        }
    }

    const fetchCustomerAddresses = async (queryString = ''): Promise<AxiosResponse<CustomerAddressesResponseSchema>> => {
        try {
            state.customerAddressesLoading.value = true
            const response = await shippingLabelsQueries.fetchCustomerAddresses(queryString)
            state.customerAddresses.value = response.data.results || []
            return response
        } finally {
            state.customerAddressesLoading.value = false
        }
    }

    const createShippingLabelFromShippingOrder = async (
        payload: CreateShippingLabelFromShippingOrderPayloadSchema
    ): Promise<AxiosResponse<ShippingLabelCreateResponse>> => {
        return await shippingLabelsQueries.createShippingLabelFromShippingOrder(payload)
    }

    const createShippingLabelFromEcommerceOrder = async (
        payload: CreateShippingLabelFromEcommerceOrderPayloadSchema
    ): Promise<AxiosResponse<ShippingLabelCreateResponse>> => {
        return await shippingLabelsQueries.createShippingLabelFromEcommerceOrder(payload)
    }

    const createShippingLabelFromCampaign = async (
        payload: CreateShippingLabelFromCampaignPayloadSchema
    ): Promise<AxiosResponse<ShippingLabelCreateResponse>> => {
        return await shippingLabelsQueries.createShippingLabelFromCampaign(payload)
    }

    const createCarrierLabel = async (
        payload: CreateCarrierLabelPayloadSchema
    ): Promise<AxiosResponse<ShippingLabelCreateResponse>> => {
        return await shippingLabelsQueries.createCarrierLabel(payload)
    }

    const createAdditionalShippingLabel = async (
        payload: CreateAdditionalShippingLabelPayloadSchema
    ): Promise<AxiosResponse<ShippingLabelCreateResponse>> => {
        return await shippingLabelsQueries.createAdditionalShippingLabel(payload)
    }

    const updateShippingLabel = async (
        payload: UpdateShippingLabelPayloadSchema
    ): Promise<AxiosResponse<ShippingLabelDetails>> => {
        return await shippingLabelsQueries.updateShippingLabel(payload)
    }

    const deleteShippingLabel = async (id: number): Promise<AxiosResponse<unknown>> => {
        return await shippingLabelsQueries.deleteShippingLabel(id)
    }

    const printShippingLabel = async (payload: PrintShippingLabelPayloadSchema): Promise<AxiosResponse<ArrayBuffer>> => {
        return await shippingLabelsQueries.printShippingLabel(payload)
    }

    const printSsccLabel = async (payload: PrintSsccLabelPayloadSchema): Promise<AxiosResponse<ArrayBuffer>> => {
        return await shippingLabelsQueries.printSsccLabel(payload)
    }

    const printBulkSsccLabels = async (payload: PrintBulkSsccLabelsPayloadSchema): Promise<AxiosResponse<ArrayBuffer>> => {
        return await shippingLabelsQueries.printBulkSsccLabels(payload)
    }

    const updateShippingLabelGood = async (payload: UpdateShippingLabelGoodPayloadSchema): Promise<AxiosResponse<unknown>> => {
        return await shippingLabelsQueries.updateShippingLabelGood(payload)
    }

    const createShippingLabelPackageConfiguration = async (
        payload: ShippingLabelPackageConfigurationPayloadSchema
    ): Promise<AxiosResponse<unknown>> => {
        return await shippingLabelsQueries.createShippingLabelPackageConfiguration(payload)
    }

    const updateShippingLabelPackageConfiguration = async (
        payload: ShippingLabelPackageConfigurationPayloadSchema
    ): Promise<AxiosResponse<unknown>> => {
        return await shippingLabelsQueries.updateShippingLabelPackageConfiguration(payload)
    }

    const deleteShippingLabelPackageConfiguration = async (id: number): Promise<AxiosResponse<unknown>> => {
        return await shippingLabelsQueries.deleteShippingLabelPackageConfiguration(id)
    }

    const setFilters = (filters: Partial<ShippingLabelsFilters>): void => {
        const nextFilters: ShippingLabelsFilters = { ...state.filters.value }

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined) {
                nextFilters[key] = value
            }
        })

        state.filters.value = nextFilters
    }

    const resetFilters = (): void => {
        state.filters.value = createInitialFilters()
    }

    const clearShippingLabel = (): void => {
        state.shippingLabel.value = null
    }

    return {
        fetchShippingLabels,
        fetchShippingLabel,
        fetchShippingLabelStatuses,
        fetchShippingLabelTypes,
        fetchShippingLabelTemplates,
        fetchShippingLabelDestinations,
        fetchShippingOrdersForShippingLabels,
        fetchOrdersForShippingLabels,
        fetchCampaignsForShippingLabel,
        fetchBoxTypes,
        fetchPackageTypes,
        fetchCountries,
        fetchCustomerAddresses,
        createShippingLabelFromShippingOrder,
        createShippingLabelFromEcommerceOrder,
        createShippingLabelFromCampaign,
        createCarrierLabel,
        createAdditionalShippingLabel,
        updateShippingLabel,
        deleteShippingLabel,
        printShippingLabel,
        printSsccLabel,
        printBulkSsccLabels,
        updateShippingLabelGood,
        createShippingLabelPackageConfiguration,
        updateShippingLabelPackageConfiguration,
        deleteShippingLabelPackageConfiguration,
        setFilters,
        resetFilters,
        clearShippingLabel
    }
}
